import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';


class ThickGridHelper extends THREE.Object3D {
    constructor(size = 10, divisions = 10, color = 0x444444, lineWidth = 2) {
        super();
        
        const step = size / divisions;
        const halfSize = size / 2;
        
        // Pre-allocate arrays for better performance
        const positions = [];
        const colors = [];
        
        // Helper function to add a line segment
        const addLine = (x1, y1, z1, x2, y2, z2, color) => {
            positions.push(x1, y1, z1, x2, y2, z2);
            colors.push(color.r, color.g, color.b, color.r, color.g, color.b);
        };
        
        // Convert color to THREE.Color object
        const gridColor = new THREE.Color(color);
        
        // Generate grid lines
        for (let i = 0; i <= divisions; i++) {
            const pos = -halfSize + (i * step);

            // Vertical lines (parallel to Z-axis)
            // From (pos, 0, -halfSize) to (pos, 0, halfSize)
            // Skip the line from (0, 0, 0) to positive X
            if (!(pos === 0)) {
                addLine(pos, 0, -halfSize, pos, 0, halfSize, gridColor);
            } else {
                // Only draw the negative side (toward -Z), not the positive Z
                addLine(pos, 0, 0, pos, 0, halfSize, gridColor);
            }

            // Horizontal lines (parallel to X-axis)
            // From (-halfSize, 0, pos) to (halfSize, 0, pos)
            // Skip the line from (0, 0, 0) to negative Z
            if (!(pos === 0)) {
                addLine(-halfSize, 0, pos, halfSize, 0, pos, gridColor);
            } else {
                // Only draw the negative side (toward -X), not the positive X
                addLine(-halfSize, 0, pos, 0, 0, pos, gridColor);
            }
        }

        
        // Create geometry and set positions/colors
        const geometry = new LineSegmentsGeometry();
        geometry.setPositions(positions);
        geometry.setColors(colors);
        
        // Create material with thick lines
        const material = new LineMaterial({
            color: 0xffffff, // This gets overridden by vertex colors
            linewidth: lineWidth,
            vertexColors: true,
            dashed: false,
            alphaToCoverage: true, // Better antialiasing
        });
        
        // Create the line segments
        const grid = new LineSegments2(geometry, material);
        grid.computeLineDistances(); // Required for proper rendering
        
        this.add(grid);
        
        // Store references for easy access
        this.grid = grid;
        this.material = material;
        this.geometry = geometry;
    }
    
    // Method to update line width
    setLineWidth(width) {
        this.material.linewidth = width;
        this.material.needsUpdate = true;
    }
    
    // Method to update color
    setColor(color = 0x444444) {
        if (!this.geometry || !this.geometry.attributes || !this.geometry.attributes.color) {
            console.warn('ThickGridHelper: Color attributes not available yet');
            return;
        }
        
        const gridColor = new THREE.Color(color);
        const colors = this.geometry.attributes.color.array;
        
        // Update all color components
        for (let i = 0; i < colors.length; i += 3) {
            colors[i] = gridColor.r;
            colors[i + 1] = gridColor.g;
            colors[i + 2] = gridColor.b;
        }
        
        this.geometry.attributes.color.needsUpdate = true;
    }
    
    // Method to update resolution for responsive line width
    updateResolution(renderer) {
        const size = renderer.getSize(new THREE.Vector2());
        this.material.resolution.set(size.width, size.height);
    }
    
    // Dispose method for cleanup
    dispose() {
        this.geometry.dispose();
        this.material.dispose();
    }
}

// Usage example:
/*
const thickGrid = new ThickGridHelper(20, 20, 0x444444, 3);
scene.add(thickGrid);

// Update line width
thickGrid.setLineWidth(5);

// Update color
thickGrid.setColor(0xff0000);

// In your render loop, update resolution for responsive lines:
thickGrid.updateResolution(renderer);
*/

export { ThickGridHelper };